import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { AtSign, Loader2, ArrowLeft, Mail, CheckCheck, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function MisMencionesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [soloNoLeidas, setSoloNoLeidas] = useState(false);
  const navigate = useNavigate();

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/bandeja-unificada/menciones/mis${soloNoLeidas ? '?solo_no_leidas=true' : ''}`);
      setItems(r.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [soloNoLeidas]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const abrirMencion = async (m) => {
    try {
      if (!m.leida) {
        await axios.post(`${API}/bandeja-unificada/menciones/${m.id}/leida`);
      }
    } catch (e) { console.error(e); }
    navigate(`/admin/bandeja-unificada?open_thread=${encodeURIComponent(m.thread_id)}`);
  };

  const marcarTodas = async () => {
    try {
      await axios.post(`${API}/bandeja-unificada/menciones/marcar-todas-leidas`);
      fetchItems();
    } catch (e) { alert('Error'); }
  };

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
      <div className="mb-4 flex items-center gap-3">
        <Link to="/admin/bandeja-unificada" className="text-sm text-purple-600 hover:underline flex items-center gap-1">
          <ArrowLeft className="w-4 h-4" /> Volver a la bandeja
        </Link>
      </div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><AtSign className="w-6 h-6 text-purple-600" /> Mis menciones</h1>
          <p className="text-sm text-gray-500">Notas internas donde tus compañeros te etiquetaron con @.</p>
        </div>
        <div className="flex gap-2">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={soloNoLeidas} onChange={e => setSoloNoLeidas(e.target.checked)} data-testid="filter-no-leidas" />
            Solo no leídas
          </label>
          <Button variant="outline" size="sm" onClick={marcarTodas} data-testid="btn-marcar-todas">
            <CheckCheck className="w-4 h-4 mr-1" /> Marcar todas como leídas
          </Button>
        </div>
      </div>

      {loading ? <Loader2 className="animate-spin w-6 h-6 text-purple-600" /> : (
        items.length === 0 ? (
          <Card><CardContent className="p-8 text-center text-sm text-gray-500">
            <AtSign className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            {soloNoLeidas ? 'No tenés menciones sin leer.' : 'Todavía no te mencionaron en ninguna nota interna.'}
          </CardContent></Card>
        ) : (
          <div className="space-y-2">
            {items.map(m => (
              <button
                key={m.id}
                onClick={() => abrirMencion(m)}
                className={`w-full text-left p-4 rounded-lg border transition ${m.leida ? 'bg-white hover:bg-gray-50 border-gray-200' : 'bg-purple-50 hover:bg-purple-100 border-purple-300'}`}
                data-testid={`mencion-${m.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center text-xs font-bold shrink-0">
                    {(m.autor_nombre || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <p className="font-semibold text-sm text-gray-900 truncate">{m.autor_nombre}</p>
                      <div className="flex items-center gap-2 shrink-0">
                        {!m.leida && <span className="w-2 h-2 rounded-full bg-purple-600" />}
                        <span className="text-xs text-gray-500">
                          {m.created_at ? new Date(m.created_at).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 mb-1 truncate flex items-center gap-1">
                      <Mail className="w-3 h-3" />
                      {m.conv_asunto || '(sin asunto)'} <span className="text-gray-400">·</span> {m.account_label || ''}
                    </p>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap line-clamp-3">{m.snippet}</p>
                    {m.leida && (
                      <p className="text-[10px] text-gray-400 flex items-center gap-1 mt-1"><Check className="w-3 h-3" /> Leída</p>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )
      )}
    </div>
  );
}
